// @flow
import React from 'react';
import LazyLoad from 'react-lazyload';
import DealerModel from 'Stores/DealerModel';
import Icon from 'Components/Icon';
import Button from 'Components/Button';
import styles from './styles.scss';
import {
  addressTracking,
  emailTracking,
  telephoneTracking,
  whatsappTracking,
} from 'Utils/trackingAttributes';
import { hasCookie, getCookie } from 'Utils';
import privacyModel from 'Stores/PrivacyModel';
import { COOKIE_KOMFORT } from 'Plugins/CookieSettings';
import Link from 'Components/Link';

type Props = {
  dealer: DealerModel,
  onOpenContact: () => void,
  showMapClicked: boolean,
  setShowMapClicked: () => void,
};

declare var PUBLIC_BASE_URL: string;

export default class Contact extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.mapWrapper = React.createRef();
    this.state = { width: 0, height: 0, blockUpdateOnResize: false };

    this.getStaticMapOrPlaceholder = this.getStaticMapOrPlaceholder.bind(this);
    this.setKomfortChange = this.setKomfortChange.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    this.updateDimensions();
  }

  resetBlockUpdateOnResize = () => {
    this.setState({
      blockUpdateOnResize: false,
    });
  };

  updateDimensions = () => {
    if (this.mapWrapper && !this.state.blockUpdateOnResize) {
      setTimeout(() => {
        this.setState({
          width: Math.round(this.mapWrapper.current.clientWidth),
          height: Math.round(this.mapWrapper.current.clientHeight),
          blockUpdateOnResize: true,
        });
      }, 300);

      setTimeout(this.resetBlockUpdateOnResize, 400);
    }
    // note: dimension limit of the google map api is 640x640
  };

  setKomfortChange() {
    privacyModel.updateCookie(COOKIE_KOMFORT, true);
    this.props.setShowMapClicked();
  }

  showMap() {
    const hascookie = hasCookie('privacySettings')
      ? JSON.parse(getCookie('privacySettings'))
      : false;

    return (
      (hascookie && hascookie.cookieTypes && hascookie.cookieTypes.komfort) ||
      this.props.showMapClicked
    );
  }

  getStaticMapOrPlaceholder(contact) {
    const showPlaceholder = !this.showMap();

    return (
      <div className={styles.mapPlaceholder}>
        <div
          id="footer-map"
          className={styles.imageWrapper}
          hidden={this.showMap() ? undefined : true}
          role="alert"
          aria-live="assertive"
        >
          {this.showMap() && (
            <LazyLoad height={240}>
              <img
                src={`https://maps.googleapis.com/maps/api/staticmap?center=${contact.lat},${contact.lng}&zoom=14&size=${this.state.width}x${this.state.height}&markers=${contact.lat},${contact.lng}&key=AIzaSyD_BjpunA5w2PPqx9psElY106LvrnWvtQI`}
                alt={`Karte der Umgebung des Standorts ${dealer.name}`}
              />
            </LazyLoad>
          )}
        </div>
        <div className={styles.placeholderImageWrapper} hidden={showPlaceholder ? undefined : true}>
          <img src={`${PUBLIC_BASE_URL}/img/map-placeholder.png`} alt="Skizze einer Karte" />
          <div className={styles.placeholderImageShadow} />
        </div>
        <div className={styles.placeholderInfo} hidden={showPlaceholder ? undefined : true}>
          <div>
            <span className={styles.plaseHolderIcon}>
              <Icon name="address" />
            </span>
            <span className={styles.plaseHolderHeader}>Autohaus-Standort</span>
          </div>
          <div className={styles.placeHolderText}>
            Gerne möchten wir Ihnen hier den Google Maps Standort unseres Autohauses anzeigen –
            akzeptieren Sie dazu den Button "Karte anzeigen". Sie können dies jederzeit in den{' '}
            <Link to="/datenschutz-praeferenzcenter/" label="Einstellungen" /> anpassen und Ihre
            Einwilligung widerrufen. Weiteres finden Sie in unserer{' '}
            <Link to="/datenschutz/" label="Datenschutzerklärung" />.
          </div>
          <div className={styles.placeHolderButton}>
            {' '}
            <Button
              className="lalal"
              label="Karte anzeigen"
              variant="secondary"
              light={true}
              ariaControls="footer-map"
              ariaLabel="Karte anzeigen und diesen Hinweis inklusive Button ausblenden"
              ariaExpanded={this.showMap()}
              onClick={this.setKomfortChange}
            />
          </div>
        </div>
      </div>
    );
  }

  onContactClick = () => {
    this.props.onOpenContact();
  };

  render() {
    const { dealer } = this.props;
    const contact = dealer.mainContact;
    const destination = `${dealer.name},${contact.street},${contact.postalCode}`
      .replace(/&/g, '%26')
      .replace(/\s+/g, '%20');

    const whatsappUrl =
      dealer.settings && dealer.settings.whatsappWidget && dealer.settings.whatsappWidget.isActive
        ? dealer.settings.whatsappWidget.whatsappUrl
        : undefined;

    return (
      <div className={styles.root}>
        <div className={styles.contactbox}>
          <div className={styles.row}>
            <div className={styles.map} ref={this.mapWrapper} data-with-whatsapp={!!whatsappUrl}>
              {this.getStaticMapOrPlaceholder(contact)}
            </div>
            <div className={styles.addressGutter}>
              <ul className={styles.address}>
                <li className={styles.addressrow}>
                  <span className={styles.headline} role="heading">
                    {dealer.name}
                  </span>
                </li>

                <li className={styles.addressrow}>
                  <span className={styles.icon}>
                    <Icon name="phone" />
                  </span>

                  <div>
                    <Link
                      to={`tel:${contact.phone}`}
                      label={contact.phone}
                      {...telephoneTracking('Dealer Contact Footer')}
                    />
                  </div>
                </li>

                <li className={styles.addressrow}>
                  <span className={styles.icon}>
                    <Icon name="address" />
                  </span>

                  <address>
                    {contact.street} {contact.houseNumber}
                    <br />D – {contact.postalCode} {contact.city}
                  </address>
                </li>

                <li className={styles.addressrow}>
                  <span className={styles.icon}>
                    <Icon name="mail" />
                  </span>

                  <div>
                    <Link
                      to={`mailto:${contact.email}`}
                      label={contact.email}
                      {...emailTracking('Dealer Contact Footer')}
                    />
                  </div>
                </li>

                {whatsappUrl && (
                  <li className={styles.addressrow}>
                    <span className={styles.icon}>
                      <img
                        role="presentation"
                        aria-hidden="true"
                        tabIndex="-1"
                        className={styles.whatsappIcon}
                        src={`${process.env.ASSET_ENV_HOST}/assets/img/icon_whatsapp_dark_blue.svg`}
                        alt={undefined}
                      />
                    </span>

                    <div>
                      <Link
                        to={whatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        {...whatsappTracking('Footer')}
                        label="WhatsApp Chat starten"
                      />
                    </div>
                  </li>
                )}

                <li className={`${styles.addressrow} ${styles.routeButton}`}>
                  <Button
                    label="Anfahrt"
                    screenReaderLabel="Routenplanung zu unserem Unternehmen in neuem Tab öffnen"
                    variant="primary"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${destination}`}
                    target="_blank"
                    additionalProps={{
                      ...addressTracking('Dealer Contact Footer'),
                    }}
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
